import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LANG } from "./data/content";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import ServicesHub from "./pages/ServicesHub";
import ServicePage from "./pages/ServicePage";
import MarketsHub from "./pages/MarketsHub";
import MarketPage from "./pages/MarketPage";
import IssuesHub from "./pages/IssuesHub";
import IssuePage from "./pages/IssuePage";
import AboutUs from "./pages/AboutUs";
import Insights from "./pages/Insights";
import Article from "./pages/Article";
import Contact from "./pages/Contact";
import Legal from "./pages/Legal";
import Sources from "./pages/Sources";
import NotFound from "./pages/NotFound";
import SubscribeConfirm from "./pages/SubscribeConfirm";
import Admin from "./pages/Admin";
import { FundingHub, ProgrammePage } from "./pages/Funding";
import Explorer from "./pages/Explorer";
import { FundingCalendar, Matcher, Cashflow } from "./pages/FundingTools";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const LocaleMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.documentElement.lang = LANG;
    const path = pathname === "/" ? "" : pathname;
    const origin = window.location.origin;
    const set = (hreflang, href) => {
      let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = "alternate";
        el.hreflang = hreflang;
        document.head.appendChild(el);
      }
      el.href = href;
    };
    set("en", origin + (path || "/"));
    set("el", origin + "/el" + path);
    set("x-default", origin + (path || "/"));
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <BrowserRouter basename={LANG === "el" ? "/el" : undefined}>
      <ScrollTop />
      <LocaleMeta />
      <Routes>
        <Route path="/admin/*" element={<Admin />} />
        <Route
          path="*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<ServicesHub />} />
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/markets" element={<MarketsHub />} />
                <Route path="/markets/:slug" element={<MarketPage />} />
                <Route path="/issues" element={<IssuesHub />} />
                <Route path="/issues/:slug" element={<IssuePage />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/about-us/our-people" element={<Navigate to="/about-us" replace />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/insights/topics/:topic" element={<Insights />} />
                <Route path="/insights/:slug" element={<Article />} />
                <Route path="/funding" element={<FundingHub />} />
                <Route path="/funding/explorer" element={<Explorer />} />
                <Route path="/funding/calendar" element={<FundingCalendar />} />
                <Route path="/funding/matcher" element={<Matcher />} />
                <Route path="/funding/cashflow" element={<Cashflow />} />
                <Route path="/funding/:country/:slug" element={<ProgrammePage />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/sources" element={<Sources />} />
                <Route path="/privacy" element={<Legal kind="privacy" />} />
                <Route path="/legal" element={<Legal kind="legal" />} />
                <Route path="/cookies" element={<Legal kind="cookies" />} />
                <Route path="/subscribe/confirm" element={<SubscribeConfirm />} />
                {/* 301-style redirects from previous URLs */}
                <Route path="/services/market-entry-and-structuring" element={<Navigate to="/services/corporate-structuring" replace />} />
                <Route path="/services/outsourced-finance" element={<Navigate to="/services/finance-operations" replace />} />
                <Route path="/services/cross-border-operations" element={<Navigate to="/services/cross-border-advisory" replace />} />
                <Route path="/sectors/fintech-and-payments" element={<Navigate to="/markets/fintech" replace />} />
                <Route path="/about" element={<Navigate to="/about-us" replace />} />
                <Route path="/approach" element={<Navigate to="/about-us" replace />} />
                <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
                <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                <Route path="/legal/terms" element={<Navigate to="/legal" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
